import {
  DermloopAlternativeImageType,
  PhotoTypes,
} from '@dermloop/static-values';
import type { PhotoType } from '@dermloop/static-values';
import { IsTempImageId } from '@dermloop/utils';
import { Transform, Type } from 'class-transformer';
import {
  IsEnum,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../base.dto';

class ConditionImageFocalPointDto implements ConditionImageFocalPoint {
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  focalPointX?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  focalPointY?: number;
}
export class CreateConditionImageDto {
  @IsOptional()
  @IsNumber()
  focalPointX: number;

  @IsOptional()
  @IsNumber()
  focalPointY: number;

  //TODO remove this when next version of apps are released with fixes for this validation error.
  @Transform(({ value }) => (value === 'dermoscopic' ? 'Dermoscopy' : value), {
    toClassOnly: true,
  })
  @IsOptional()
  @IsIn(PhotoTypes)
  type?: PhotoType;

  /**
   * Open image-capture key (form-renderer path). When present, `type` may be
   * omitted — the server resolves the `DermloopImageType` row from this key.
   * At least one of `type` / `imageType` must be provided; the `@ValidateIf`
   * (unlike a plain `@IsOptional`) enforces that mutual requirement — it runs
   * the string validators only when the key is present or `type` is absent.
   */
  @ValidateIf((o) => o.imageType != null || o.type == null)
  @IsString()
  @IsNotEmpty({ message: 'Either type or imageType must be provided' })
  imageType?: string;

  @IsOptional()
  @IsNumber()
  sharpness?: number;

  @IsOptional()
  @IsNumber()
  alternativeImageSharpness?: number;

  @IsOptional()
  @IsEnum(DermloopAlternativeImageType)
  alternativeImageType?: DermloopAlternativeImageType;

  @IsOptional()
  @IsString()
  conditionTriageId?: string;

  @IsOptional()
  @IsString()
  /** @deprecated Use conditionTriageId */
  lesionTriageId?: string;
}

export class CreateAndUploadConditionImageDto {
  @IsString()
  @IsTempImageId()
  temporaryImageId: string;

  @ValidateNested()
  @Type(() => ConditionImageFocalPointDto)
  image: ConditionImageFocalPointDto;

  @IsNotEmpty()
  @IsString()
  uri: string;

  @IsOptional()
  @IsString()
  flashUri?: string;

  @IsOptional()
  @IsString()
  conditionId?: string;

  @IsOptional()
  @IsString()
  /** @deprecated Use conditionId */
  skinConditionId?: string;

  @IsNotEmpty()
  @IsString()
  triageId: string;

  @IsOptional()
  @IsIn(PhotoTypes)
  type?: PhotoType;

  /**
   * Open image-capture key (form-renderer path). Forwarded to the server, which
   * resolves the image-type row and derives the legacy `PhotoType`. At least
   * one of `type` / `imageType` must be provided.
   */
  @ValidateIf((o) => o.imageType != null || o.type == null)
  @IsString()
  @IsNotEmpty({ message: 'Either type or imageType must be provided' })
  imageType?: string;

  @IsOptional()
  @IsNumber()
  sharpness?: number;

  @IsOptional()
  @IsNumber()
  alternativeImageSharpness?: number;
}

export class UpdateConditionImageDto {
  @IsOptional()
  @IsNumber()
  focalPointX?: number;

  @IsOptional()
  @IsNumber()
  focalPointY?: number;

  @IsOptional()
  @IsString()
  conditionTriageId?: string;

  @IsOptional()
  @IsString()
  /** @deprecated Use conditionTriageId */
  lesionTriageId?: string;
}
/**
 * @description Legacy urls with old naming convention.
 * @deprecated Should not be used in any new work with images.
 */
class ConditionImageDtoLegacyUrls extends BaseDto {
  constructor(lesionImageDto: Partial<ConditionImageDto>) {
    super();
    if (lesionImageDto) {
      this.thumbnailXsIdentifier = lesionImageDto.thumbnailXsUrl;
      this.thumbnailSIdentifier = lesionImageDto.thumbnailSUrl;
      this.thumbnailMIdentifier = lesionImageDto.thumbnailMUrl;
      this.thumbnailLIdentifier = lesionImageDto.thumbnailLUrl;
      this.thumbnailXLIdentifier = lesionImageDto.thumbnailXlUrl;

      this.alternativeImageThumbnailXsIdentifier =
        lesionImageDto.alternativeImageThumbnailXsUrl;
      this.alternativeImageThumbnailSIdentifier =
        lesionImageDto.alternativeImageThumbnailSUrl;
      this.alternativeImageThumbnailMIdentifier =
        lesionImageDto.alternativeImageThumbnailMUrl;
      this.alternativeImageThumbnailLIdentifier =
        lesionImageDto.alternativeImageThumbnailLUrl;
      this.alternativeImageThumbnailXLIdentifier =
        lesionImageDto.alternativeImageThumbnailXlUrl;
    }
  }
  /**
   * @deprecated legacy identifier use urls instead
   */
  thumbnailXsIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  thumbnailSIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  thumbnailMIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  thumbnailLIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  thumbnailXLIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  alternativeImageThumbnailXsIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  alternativeImageThumbnailSIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  alternativeImageThumbnailMIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  alternativeImageThumbnailLIdentifier?: string;
  /**
   * @deprecated legacy identifier use urls instead
   */
  alternativeImageThumbnailXLIdentifier?: string;
}

export class ConditionImageDto extends ConditionImageDtoLegacyUrls {
  constructor(data?: Partial<ConditionImageDto>) {
    super(data ?? {});
    if (data) {
      Object.assign(this, data);
    }
  }

  @IsOptional()
  @IsString()
  thumbnailXsUrl?: string;

  @IsOptional()
  @IsString()
  thumbnailSUrl?: string;

  @IsOptional()
  @IsString()
  thumbnailMUrl?: string;

  @IsOptional()
  @IsString()
  thumbnailLUrl?: string;

  @IsOptional()
  @IsString()
  thumbnailXlUrl?: string;

  @IsOptional()
  @IsString()
  url?: string;

  @IsOptional()
  @IsString()
  uploadUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageThumbnailXsUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageThumbnailSUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageThumbnailMUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageThumbnailLUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageThumbnailXlUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageUrl?: string;

  @IsOptional()
  @IsString()
  alternativeImageUploadUrl?: string;

  @IsOptional()
  @IsEnum(DermloopAlternativeImageType)
  alternativeImageType?: DermloopAlternativeImageType;

  @IsOptional()
  @IsNumber()
  focalPointX?: number;

  @IsOptional()
  @IsNumber()
  focalPointY?: number;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  imageTypeId?: string;

  /** The image type's form-language key (e.g. `muco-palate`); set by the server when the relation is loaded. */
  imageTypeKey?: string;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  medicalUnitId?: string;

  conditionTriageId?: string;

  /** @deprecated Use conditionTriageId */
  lesionTriageId?: string;

  @IsOptional()
  @IsNumber()
  sharpness?: number;

  @IsOptional()
  @IsNumber()
  alternativeImageSharpness?: number;
}

/**
 * DTO for the condition image urls
 */
export interface ConditionImageUrlsDto {
  url?: string;
  thumbnailXsUrl?: string;
  thumbnailSUrl?: string;
  thumbnailMUrl?: string;
  thumbnailLUrl?: string;
  thumbnailXlUrl?: string;
}

export type ConditionImageFocalPoint = Pick<
  ConditionImageDto,
  'focalPointX' | 'focalPointY'
>;

export class SearchConditionImagesDto {
  @IsOptional()
  @IsUUID()
  createdByUserId?: string;

  @IsOptional()
  @IsString()
  imageTypeName?: string;

  @IsOptional()
  @IsNumber()
  @Max(50)
  take?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;
}

/** @deprecated Use SearchConditionImagesDto */
export { SearchConditionImagesDto as SearchLesionImagesDto };
